import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin } from 'lucide-react';
import Header from '../components/Header';
import { venuesAPI, handleApiError } from '../utils/api';

// Les icônes par défaut de Leaflet référencent des images via des chemins
// relatifs qui ne survivent pas au bundling webpack/CRA. On les repointe
// vers le CDN unpkg (solution standard pour react-leaflet + CRA).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const FRANCE_CENTER = [46.6, 2.5];
const DEFAULT_ZOOM = 6;

const VenuesMapPage = () => {
  const [venues, setVenues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadVenues = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await venuesAPI.getAll();
      setVenues(response.data || []);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setError(`${errTitle} : ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVenues();
  }, [loadVenues]);

  const locatedVenues = venues.filter(
    (v) => v.isActive !== false && v.latitude != null && v.longitude != null
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
              <h1 className="text-3xl font-bold flex items-center">
                <MapPin className="mr-3" size={32} />
                Carte des Lieux
              </h1>
              <p className="mt-2 text-blue-100">
                {locatedVenues.length} lieu{locatedVenues.length > 1 ? 'x' : ''} de poker référencé
                {locatedVenues.length > 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Chargement de la carte...
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{ height: '70vh' }}>
              <MapContainer
                center={FRANCE_CENTER}
                zoom={DEFAULT_ZOOM}
                scrollWheelZoom
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {locatedVenues.map((venue) => (
                  <Marker key={venue.id} position={[Number(venue.latitude), Number(venue.longitude)]}>
                    <Popup>
                      <div className="text-sm">
                        <p className="font-bold">{venue.name}</p>
                        {venue.address && <p>{venue.address}</p>}
                        {(venue.city || venue.country) && (
                          <p>
                            {venue.city}
                            {venue.city && venue.country ? ', ' : ''}
                            {venue.country}
                          </p>
                        )}
                        {venue.websiteUrl && (
                          <a
                            href={venue.websiteUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            Site web
                          </a>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VenuesMapPage;
